/* ==========================================================================
   fe-plan-tabs — the tablist behind the plans card.

   Implements the ARIA tabs pattern with manual activation: arrow keys move
   focus and select, Home/End jump to the ends. Manual activation (rather than
   selecting on focus) is the right choice here because each panel is a large
   block of content — selecting as focus passes through would fire off several
   panel swaps on the way to the tab someone actually wants.

   Without JS the section renders every panel stacked and hides the strip, via
   the <noscript> block the section prints — so the content is never trapped
   behind a control that cannot work.
   ========================================================================== */

if (!customElements.get('fe-plan-tabs')) {
  customElements.define(
    'fe-plan-tabs',
    class FePlanTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (this.tabs.length < 2) return;

        this.panels = this.tabs.map((tab) => {
          const id = tab.getAttribute('aria-controls');
          return id ? this.querySelector(`[id="${id}"]`) : null;
        });

        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKeydown);

        this.buildSlider();

        // Start from whatever the markup marked selected, so the server-rendered
        // state and the JS state cannot disagree on first paint.
        const current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
        this.select(current === -1 ? 0 : current, { focus: false });

        // Equal-width columns, so the tabs only resize when the strip does.
        if (this.tablist && 'ResizeObserver' in window) {
          this.observer = new ResizeObserver(() => this.moveSlider());
          this.observer.observe(this.tablist);
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKeydown);
        this.observer?.disconnect();
      }

      // The sliding ground is created here rather than printed by the section:
      // it is a JS-only affordance, so building it in JS is what guarantees a
      // no-JS visitor never sees an indicator that cannot move.
      buildSlider() {
        this.tablist = this.querySelector('[role="tablist"]');
        if (!this.tablist) return;

        this.slider = document.createElement('span');
        this.slider.className = 'fe-plans__tab-slider';
        this.slider.setAttribute('aria-hidden', 'true');
        this.tablist.append(this.slider);
        this.tablist.dataset.slider = 'on';
      }

      moveSlider() {
        if (!this.slider) return;

        const tab = this.tabs[this.current];
        if (!tab) return;

        // offsetLeft is measured against the strip, which is the slider's
        // containing block — so this stays correct once the strip scrolls
        // sideways on a narrow screen.
        this.slider.style.setProperty('--fe-slider-x', `${tab.offsetLeft}px`);
        this.slider.style.setProperty('--fe-slider-w', `${tab.offsetWidth}px`);
      }

      onClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab) return;
        const index = this.tabs.indexOf(tab);
        if (index !== -1) this.select(index, { focus: false });
      };

      onKeydown = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab) return;

        const from = this.tabs.indexOf(tab);
        const last = this.tabs.length - 1;
        let next;

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = from === last ? 0 : from + 1;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = from === 0 ? last : from - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.select(next, { focus: true });
      };

      select(index, { focus }) {
        const first = this.current === undefined;
        this.current = index;

        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          // Only the selected tab stays in the tab order; the arrow keys reach
          // the others. This is what makes a tablist one stop, not four.
          tab.setAttribute('tabindex', on ? '0' : '-1');

          const panel = this.panels[i];
          if (panel) panel.hidden = !on;
        });

        this.moveSlider();

        // Transitions stay off for the very first placement, then come on a
        // frame later, so the slider appears already under the active tab
        // instead of flying in from the strip's left edge.
        if (first) {
          requestAnimationFrame(() => {
            if (this.tablist) this.tablist.dataset.sliderReady = '';
          });
        }

        if (focus) this.tabs[index].focus();

        // A tab the slider has just moved to is no use sitting off-screen.
        if (!first && this.tablist && this.tablist.scrollWidth > this.tablist.clientWidth) {
          this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      }
    }
  );
}
